/**
 * `canDo` — decisão de autorização baseada em permissão (TASK-15, REQ-05/REQ-06/REQ-08).
 *
 * Paralelo TypeScript da API que `Azim.Authorization` expõe em C# (TASK-14) sobre o MESMO
 * modelo (`permissionModel.ts`). Não substitui `shared/rbac` (RBAC atual por papel, fora de
 * escopo desta task) — convive em paralelo até a Onda 5 (TASK-23) migrar as telas.
 */
import { resolveReach } from "./permissionModel.js";
import type { Reach, RoleKey } from "./types.js";

/**
 * Contexto do recurso avaliado. `own`: o recurso pertence ao usuário atual? `unit`: o recurso
 * está na mesma unidade de negócio do usuário atual? Omitido (ou `undefined`) equivale a "não
 * sei" — `canDo` nega por padrão quando o alcance exige uma informação de contexto que não foi
 * fornecida (mais seguro que assumir positivo).
 */
export interface CanDoContext {
  readonly own?: boolean;
  readonly unit?: boolean;
}

/**
 * Decide se `role` pode executar `permissionKey` sobre um recurso descrito por `context`.
 * Nunca lança — permissão desconhecida ou retirada resolve `"none"` (nega). Alcance:
 * - `"none"`: nega sempre.
 * - `"own"`: permite só quando `context.own === true`.
 * - `"unit"`: permite quando `context.unit === true` OU `context.own === true` (um recurso
 *   próprio do usuário está, por definição, dentro da própria unidade dele).
 * - `"org"`: permite sempre.
 */
export function canDo(role: RoleKey, permissionKey: string, context?: CanDoContext): boolean {
  const reach = resolveReach(role, permissionKey);
  return reachAllows(reach, context);
}

function reachAllows(reach: Reach, context?: CanDoContext): boolean {
  switch (reach) {
    case "none":
      return false;
    case "own":
      return context?.own === true;
    case "unit":
      return context?.unit === true || context?.own === true;
    case "org":
      return true;
  }
}
